import React, { Fragment, useState, useEffect } from "react";
import { useFormikContext } from "formik";
import Overlay from "components/Overlay";
import CheckoutBuilder from "../CheckoutBuilder";
import CheckoutPreview from "../CheckoutPreview";
import { STATE_KEYS } from "utils/constants/state";
import { useProductsPrices } from "utils/selectors";
import get from "lodash/get";
import set from "lodash/set";
import isEmpty from "lodash/isEmpty";
import cloneDeep from "lodash/cloneDeep";
import { reduceIdMap } from "utils/data";

const CHECKOUT_PICKER_INTENT = {
  SELECT_RESULT: "SELECT_RESULT"
};

const CheckoutShow = ({ name, activeIndex, uuid }) => {
  const { values, touched, setValues, setFieldTouched } = useFormikContext();

  const prodCheckoutsPath = `${name}.${STATE_KEYS.PRODUCT_COLLECTION.CONFIG_PRODUCTS_CHECKOUTS}`;
  const productCheckoutConfigPath = `${prodCheckoutsPath}.${uuid}`;
  const checkout = get(values, productCheckoutConfigPath);
  const missingCheckout = isEmpty(checkout);

  const productPath = `${name}.${STATE_KEYS.PRODUCT_COLLECTION.PRODUCTS}[${activeIndex}]`;
  const { products } = useProductsPrices();
  const productMap = reduceIdMap(products);

  /**
   * Allow the user to toggle between product and price pick intent
   */
  const [intent, setIntent] = useState(null);
  const [autoShow, setAutoShow] = useState(false);
  let overlayContent;
  const showOverlay = Boolean(intent);

  /**
   * Show the checkout builder when
   * - no checkout is set
   * - overlay is not visible
   * - auto show hasnt been attempted
   */
  useEffect(() => {
    if (missingCheckout && !showOverlay && !autoShow) {
      setAutoShow(true);
      setIntent(CHECKOUT_PICKER_INTENT.SELECT_RESULT);
    }
  }, [missingCheckout, showOverlay, autoShow]);

  const setResult = (builtCheckoutConfig) => {
    const updatedValues = cloneDeep(values);
    set(updatedValues, productCheckoutConfigPath, builtCheckoutConfig);

    const primaries = builtCheckoutConfig.primaries || [];
    const primaryMatch = primaries.find(({ product }) => productMap[product]);

    /**
     * Reset product title and description if
     * - no checkout
     * - the primary match product is not in the checkout primaries
     */
    if (!checkout || primaryMatch) {
      const currentMatch = Boolean(
        checkout &&
          checkout.primaries &&
          checkout.primaries.find(
            ({ product }) => product === primaryMatch.product
          )
      );

      if (!currentMatch && primaryMatch && primaryMatch.product) {
        const productMatch = productMap[primaryMatch.product];
        set(updatedValues, `${productPath}.title`, productMatch.name);
        set(
          updatedValues,
          `${productPath}.description`,
          productMatch.description
        );
      }
    }
    setValues(updatedValues);

    const isTouched = get(touched, productCheckoutConfigPath);
    if (!isTouched) {
      setTimeout(() => setFieldTouched(productCheckoutConfigPath, true), 0);
    }
    setIntent(null);
  };

  if (intent === CHECKOUT_PICKER_INTENT.SELECT_RESULT) {
    overlayContent = (
      <CheckoutBuilder
        uuid={uuid}
        name={name}
        activeIndex={activeIndex}
        goBack={setResult}
      />
    );
  }

  const hasComboResult = !isEmpty(checkout);

  const previewProps = {
    name,
    onClick: () => setIntent(CHECKOUT_PICKER_INTENT.SELECT_RESULT),
    checkout: checkout,
    clearResult: hasComboResult ? () => setResult({}) : null
  };

  return (
    <Fragment>
      <CheckoutPreview {...previewProps} />
      {showOverlay && overlayContent && (
        <Overlay hideOverlay={() => setIntent(null)}>{overlayContent}</Overlay>
      )}
    </Fragment>
  );
};

export default CheckoutShow;
